import React, { useCallback, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useService } from '../../../services/services';
import GuestLayout from '../../../components/external/guest-layout';
import AuthButton from '../../../components/external/auth-button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import authCss from '../auth-forms.module.css';
import css from '../../profile/index.module.css';
import { LOGIN } from '../../../router/routes';
import * as yup from 'yup';
import debounce from 'awesome-debounce-promise';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { yupResolver } from '@hookform/resolvers/yup';
import { parseError } from '../../../services/utils';

export default function ResetPassword() {
  const { t } = useTranslation();
  const history = useHistory();
  const { authService } = useService();
  const { token }: { token: string } = useParams();
  const [isPasswordVisible, setPasswordVisibility] = useState(false);
  const [isPasswordConfirmationVisible, setPasswordConfirmationVisibility] =
    useState(false);

  const [resetPasswordFormSchema] = useState(() =>
    yup.object().shape({
      password: yup
        .string()
        .max(255)
        .required()
        .test(
          'passwordValid',
          'Password does not follow the rules',
          debounce(
            (value) =>
              authService
                .validatePassword(value)
                .pipe(
                  catchError(() => of(false)),
                  map((value) => value !== false)
                )
                .toPromise(),
            1500
          )
        ),
      password_confirmation: yup
        .string()
        .max(255)
        .required()
        .oneOf(
          [yup.ref('password')],
          'pages.profile.messages.new_password_confirmation_error'
        ),
    })
  );

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: { password: '', password_confirmation: '' },
    resolver: yupResolver(resetPasswordFormSchema),
  });
  const { search } = useLocation();

  const submit = (data: {
    password: string;
    password_confirmation: string;
  }) => {
    authService.csrf().subscribe(() => {
      authService
        .resetPassword(
          new URLSearchParams(search).get('email'),
          token,
          data.password,
          data.password_confirmation
        )
        .subscribe((response) => {
          history.push(LOGIN);
        });
    });
  };

  const togglePassword = useCallback(
    () => setPasswordVisibility(!isPasswordVisible),
    [setPasswordVisibility, isPasswordVisible]
  );
  const togglePasswordConfirmation = useCallback(
    () => setPasswordConfirmationVisibility(!isPasswordConfirmationVisible),
    [setPasswordConfirmationVisibility, isPasswordConfirmationVisible]
  );

  return (
    <GuestLayout>
      <div className={authCss['container']}>
        <form
          className={authCss['form']}
          onSubmit={handleSubmit(submit)}
          autoComplete="off"
        >
          <h1>{t('pages.external.reset-password.title')}</h1>
          <div
            className={`${authCss['field']} ${
              errors.password ? authCss['has-error'] : ''
            }`}
          >
            <label htmlFor="password">
              {t('pages.external.reset-password.password')}:
            </label>
            <input
              type={isPasswordVisible ? 'text' : 'password'}
              id="password"
              name="password"
              className={authCss['input']}
              {...register('password')}
            />
            <span
              className={authCss['password-switcher']}
              onClick={togglePassword}
            >
              {isPasswordVisible ? (
                <FontAwesomeIcon icon={faEyeSlash} />
              ) : (
                <FontAwesomeIcon icon={faEye} />
              )}
            </span>
          </div>
          {errors.password && (
            <span className={authCss['error']}>
              {t(parseError('password', errors)?.message)}
            </span>
          )}
          <span className={css['password-info-message']}>
            {t('pages.profile.messages.password')}
            <ul className={css['password-criteria']}>
              <li>{t('pages.profile.messages.letter')}</li>
              <li>{t('pages.profile.messages.special')}</li>
              <li>{t('pages.profile.messages.number')}</li>
            </ul>
          </span>
          <div
            className={`${authCss['field']} ${
              errors.password_confirmation ? authCss['has-error'] : ''
            }`}
          >
            <label htmlFor="password_confirmation">
              {t('pages.external.reset-password.password_confirmation')}:
            </label>
            <input
              type={isPasswordConfirmationVisible ? 'text' : 'password'}
              id="password_confirmation"
              name="password_confirmation"
              className={authCss['input']}
              {...register('password_confirmation')}
            />
            <span
              className={authCss['password-switcher']}
              onClick={togglePasswordConfirmation}
            >
              {isPasswordConfirmationVisible ? (
                <FontAwesomeIcon icon={faEyeSlash} />
              ) : (
                <FontAwesomeIcon icon={faEye} />
              )}
            </span>
          </div>
          {errors.password_confirmation && (
            <span className={authCss['error']}>
              {t(parseError('password_confirmation', errors)?.message)}
            </span>
          )}
          <div className={authCss['buttons']}>
            <AuthButton
              label={t('pages.external.reset-password.btn')}
              type="submit"
              disabled={!isValid}
            />
          </div>
        </form>
      </div>
    </GuestLayout>
  );
}
